import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

const ADMIN_BIZ_KEY = "kp_admin_biz_id";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const u = localStorage.getItem("kp_user");
    return u ? JSON.parse(u) : null;
  });
  const [loading, setLoading] = useState(false);
  // Super Admin business switcher: id of the business the platform admin
  // is currently managing. `null` means "managing my own (ClientFlow HQ)".
  const [adminBusinessId, setAdminBusinessIdState] = useState(
    () => localStorage.getItem(ADMIN_BIZ_KEY) || null
  );

  const isPlatformAdmin = !!user?.is_platform_admin;
  // Effective: only honour the switcher for platform admins. For everyone
  // else this is always null regardless of stale localStorage.
  const effectiveAdminBusinessId = isPlatformAdmin ? adminBusinessId : null;

  const setAdminBusinessId = useCallback((id) => {
    if (id) {
      localStorage.setItem(ADMIN_BIZ_KEY, id);
    } else {
      localStorage.removeItem(ADMIN_BIZ_KEY);
    }
    setAdminBusinessIdState(id || null);
  }, []);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    localStorage.setItem("kp_token", res.data.access_token);
    localStorage.setItem("kp_user", JSON.stringify(res.data.user));
    // Reset any stale admin-business selection from a previous session.
    localStorage.removeItem(ADMIN_BIZ_KEY);
    setAdminBusinessIdState(null);
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (payload) => {
    const res = await api.post("/auth/register", payload);
    localStorage.setItem("kp_token", res.data.access_token);
    localStorage.setItem("kp_user", JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = () => {
    localStorage.removeItem("kp_token");
    localStorage.removeItem("kp_user");
    localStorage.removeItem(ADMIN_BIZ_KEY);
    setAdminBusinessIdState(null);
    setUser(null);
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await api.get("/auth/me");
      localStorage.setItem("kp_user", JSON.stringify(res.data));
      setUser(res.data);
    } catch {
      logout();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (localStorage.getItem("kp_token") && !user) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthCtx.Provider
      value={{
        user,
        login,
        register,
        logout,
        refresh,
        loading,
        isPlatformAdmin,
        adminBusinessId: effectiveAdminBusinessId,
        setAdminBusinessId,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
